<script setup lang="ts">
import { computed } from 'vue'
import MmIcon from './MmIcon.vue'
import MmStateArt from './MmStateArt.vue'
import type { MmIconName } from './icons'
const props = withDefaults(defineProps<{
  title: string; description?: string; icon?: MmIconName;
  heading?: 'h1' | 'h2' | 'h3'; kind?: 'empty' | 'error'; page?: boolean;
  illustration?: 'empty' | 'not-found' | 'error' | 'generating' | 'none'; compact?: boolean;
}>(), { icon: 'disc', heading: 'h3', kind: 'empty', page: false, compact: false })
const art = computed(() => props.illustration ?? (props.kind === 'error' ? 'error' : ['disc', 'library'].includes(props.icon) ? 'empty' : 'none'))
</script>
<template>
  <section class="mm-feedback" :class="{ 'mm-feedback-page': page, 'mm-feedback-compact': compact }" :role="kind === 'error' ? 'alert' : 'status'">
    <MmStateArt v-if="art !== 'none'" :kind="art" />
    <MmIcon v-else class="mm-feedback-icon" :name="icon" :size="32" aria-hidden="true" />
    <div class="mm-feedback-copy">
      <p v-if="art === 'not-found'" class="mm-feedback-code" aria-hidden="true">404 / TRACK NOT FOUND</p>
      <component :is="heading" class="mm-feedback-title">{{ title }}</component>
      <p v-if="description" class="mm-feedback-description">{{ description }}</p>
    </div>
    <div v-if="$slots.default" class="mm-feedback-actions"><slot /></div>
  </section>
</template>
<style scoped>
.mm-feedback { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--s-5); min-width: 0; padding: var(--s-10) var(--s-5); color: var(--text); text-align: center; }
.mm-feedback-icon { color: var(--text-3); flex: none; }
.mm-feedback-copy { display: grid; justify-items: center; gap: var(--s-2); max-width: 36ch; }
.mm-feedback .mm-feedback-title { margin: 0; font: 600 var(--t-body-l)/1.4 var(--font-ui); text-wrap: balance; overflow-wrap: anywhere; }
.mm-feedback .mm-feedback-description { margin: 0; font: 400 var(--t-small)/1.7 var(--font-ui); color: var(--text-2); text-wrap: pretty; overflow-wrap: anywhere; }
.mm-feedback-code { margin: 0 0 var(--s-2); color: var(--text-3); font: 400 var(--t-micro)/1.5 var(--font-mono); letter-spacing: .16em; }
.mm-feedback-actions { display: flex; justify-content: center; flex-wrap: wrap; gap: var(--s-3); max-width: 100%; }
.mm-feedback-actions :deep(.btn) { min-height: 44px; max-width: 100%; white-space: normal; }
.mm-feedback-page { padding-block: var(--s-12); gap: var(--s-6); }
.mm-feedback-page :deep(.mm-state-art) { width: 280px; height: 186px; }
.mm-feedback-page .mm-feedback-title { font: 600 var(--t-h1)/1.25 var(--font-display); letter-spacing: -.03em; }
.mm-feedback-page .mm-feedback-description { font-size: var(--t-body); }
.mm-feedback-compact { padding: var(--s-6) var(--s-4); gap: var(--s-3); }
.mm-feedback-compact :deep(.mm-state-art) { width: 160px; height: 106px; }
@media (max-width: 479px) { .mm-feedback-page { padding-inline: var(--s-4); } .mm-feedback-page :deep(.mm-state-art) { width: 240px; height: 160px; } }
</style>
